import React from 'react';
import { Card, Typography, Box, Divider, IconButton, Button } from '@mui/material';
import { AddAlarm, AlarmOn } from '@mui/icons-material';
import { formatDataHora } from 'src/utils/dateUtils';
import { Image } from 'src/components';
import FlatIcon, { FlatIconOptions } from 'src/components/ui/FlatIcon';

const CardWithMultiplesInfos = ({
  titulo,
  conteudo = [],
  tipoPedido,
  extraContent = null,
  extraContentDirection = 'column',
  icon = null,
  iconLabelTop,
  iconLabelBottom,
  acoes = [],
  borderLeftColor = null,
  cardStyle = {},
  onCardClick = null,
}) => {
  const extraContentToRow =
    extraContentDirection === 'row' ?
      {
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'row',
        gap: 1,
      }
    : { display: 'flex', flexDirection: 'column', gap: 1 };

  const isDesktop = window.innerWidth > 768;

  const isDateIso = (value) => {
    if (!value || typeof value !== 'string') return false;
    const date = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) && !isNaN(new Date(value).getTime());

    return formatDataHora(date);
  };

  const drawGroupStyleUserAction = ({ groupFields }) => {
    const [valA, valB, valC] = groupFields;
    return (
      <Box sx={{ py: 0.5 }}>
        {valA && <Typography sx={{ lineHeight: 1, mt: 'auto', fontSize: 14, textAlign: 'right' }}>{valA}</Typography>}
        {valC ?
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: 0.5,
              }}>
              <AddAlarm sx={{ fontSize: 16 }} />
              <Typography sx={{ color: '#999', fontSize: 10 }}>
                {isDateIso(valB) ? formatDataHora(valB) : valB || ''}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
              <AlarmOn sx={{ fontSize: 16 }} />
              <Typography sx={{ color: '#999', fontSize: 10 }}>
                {isDateIso(valC) ? formatDataHora(valC) : valC || ''}
              </Typography>
            </Box>
          </>
        : <Typography
            sx={{
              lineHeight: 1,
              mb: 'auto',
              color: '#999',
              fontSize: 10,
              textAlign: 'right',
            }}>
            {isDateIso(valB) ? formatDataHora(valB) : valB || ''}
          </Typography>
        }
      </Box>
    );
  };

  const drawGroupStyleDivergence = ({ groupFields, applyDivergenceColor = true }) => {
    const [valA, valB] = groupFields;
    const bothDates = isDateIso(valA) && isDateIso(valB);
    return (
      <>
        <span>{isDateIso(valA) ? formatDataHora(valA) : valA || '-'}</span> |{' '}
        <span
          style={{
            color: !bothDates && valA !== valB && applyDivergenceColor ? 'red' : 'inherit',
          }}>
          {isDateIso(valB) ? formatDataHora(valB) : valB || '-'}
        </span>
      </>
    );
  };

  return (
    <Card
      sx={{
        p: 2,
        mb: 2,
        width: '100%',
        borderLeft: borderLeftColor ? `5px solid ${borderLeftColor}` : 'none',
        ...cardStyle,
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isDesktop ? 'row' : 'column',
          gap: 1.5,
          width: '100%',
        }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              width: isDesktop ? 'auto' : '100%',
            }}>
            {typeof icon === 'string' ?
              <img
                src={icon}
                alt=''
                style={{
                  height: 50,
                  width: 50,
                  borderRadius: 5,
                  objectFit: 'cover',
                }}
              />
            : icon}
            <Box>
              {iconLabelTop && <Typography fontSize={12}>{iconLabelTop}</Typography>}
              {iconLabelBottom && (
                <Typography
                  variant='caption'
                  fontSize={12}>
                  {iconLabelBottom}
                </Typography>
              )}
            </Box>
          </Box>
          {onCardClick && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <FlatIcon
                icon={FlatIconOptions.search}
                onClick={(event) => onCardClick && onCardClick(event)}
              />
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 0.5 }}>
          {!titulo.render ?
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Typography
                variant='subtitle1'
                fontWeight={600}>
                {titulo.label}
              </Typography>
              <Typography
                variant='subtitle1'
                fontWeight={500}>
                {titulo.value}
              </Typography>
            </Box>
          : <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1 }}>{titulo.render()}</Box>}

          {conteudo.map((line, idx) => {
            const { label, value, fontSize, bold, align, rowSx } = line;
            const isGroup = value && typeof value === 'object' && !!value.groupType;

            const justifyContent =
              align === 'center' ? 'center'
              : align === 'right' ? 'flex-end'
              : align === 'left' ? 'flex-start'
              : 'space-between';

            return (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  justifyContent,
                  width: '100%',
                  ...(rowSx || {}),
                }}>
                {label ?
                  <Typography
                    variant='caption'
                    color='textSecondary'>
                    {label}
                  </Typography>
                : null}

                {value !== undefined && value !== null ?
                  !value.render ?
                    !isGroup ?
                      <Typography
                        variant='body2'
                        fontSize={fontSize || 12}
                        fontWeight={bold ? 700 : 400}>
                        {value}
                      </Typography>
                    : value.groupType === 'userAction' ?
                      drawGroupStyleUserAction(value)
                    : drawGroupStyleDivergence(value)
                  : value.render()
                : null}
              </Box>
            );
          })}
        </Box>
        {tipoPedido && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              backgroundColor: '#eee',
              borderRadius: 1,
              px: 1,
              py: 0.5,
              gap: 1,
              ml: 'auto',
            }}>
            <Typography fontSize={12}>{tipoPedido}</Typography>
          </Box>
        )}
        {extraContent && <Box sx={extraContentToRow}>{extraContent}</Box>}
      </Box>

      {acoes.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              mt: 1,
              width: '100%',
            }}>
            {acoes.map((acao, i) => (
              <IconButton
                key={i}
                onClick={acao.onClick}
                disabled={acao.disabled}
                size='small'>
                {acao.icon}
              </IconButton>
            ))}
          </Box>
        </>
      )}
    </Card>
  );
};

export default CardWithMultiplesInfos;
